import React from "react";
import { endpoints } from "config";
import * as Yup from "yup";
import {
  requiredString,
  optionalString,
  optionalNumber,
  optionalBoolean,
} from "helpers/validation/yupSchemaFactory";
import { WorkflowCreateEditFields, WorkflowFields } from "./models";
import { FieldRenderers } from "@avamae/formbuilder";
import { WorkflowListData, idColumn } from "./WorkflowConfigurations";
import Entity from "components/Entity";
import {
  singleMultiSelectFieldRenderer,
  singleMultiSelectReadOnlyFieldRenderer,
} from "components/helpers/CustomFieldRenderers/SingleMultiSelectFieldRenderer.tsx/SingleMultiSelectFieldRenderer";
import { fileUploadFieldRenderer } from "components/helpers/FieldRenderers/FileUpload/fileUploadFieldRenderer";
import { booleanV2Renderer } from "components/BooleanV2/BooleanV2";
import { dateTimeV2Renderer } from "components/DateTimeFieldV2/DateTimeFieldV2";
import {
  searchListFieldRenderer,
  searchListReadOnlyFieldRenderer,
} from "components/helpers/CustomFieldRenderers/SearchListFieldRenderer/SearchListRenderer";
import { workflowBuilderFieldRenderer } from "components/helpers/CustomFieldRenderers/WorkflowBuilderFieldRenderer/WorkflowBuilderFieldRenderer";
import {
  actionLinkTextConfig,
  endpointConfig,
  linkConfig,
  onActionLinkClick,
  onLinkClick,
} from "./WorkflowCreateEdit/workflowCreateEditDetailsConfig";
import { stringReadOnlyEditRenderer } from "components/helpers/CustomFieldRenderers/CustomString/StringReadOnlyEditFieldRenderer";
import { CrudOptions } from "components/Category/CrudMenu";
import { stringV2Renderer } from "components/helpers/CustomFieldRenderers/CustomString/StringV2";

export const workflowValidationSchema = Yup.object({
  id: optionalNumber(),
  workflowName: requiredString({
    message: "This is a required field.",
    maxLength: 200,
    maxMessage: "This field has a maximum of 200 characters.",
  }),
  description: optionalString({
    maxLength: 1000,
    maxMessage: "This field has a maximum of 1000 characters.",
  }),
  programmeID: Yup.number()
    .integer()
    .nullable()
    .when("bUniversal", {
      is: false,
      then: (schema) => schema.integer().nullable().required("This is a required field."),
    }),
  bUniversal: optionalBoolean(),
  bActive: optionalBoolean(),
});

type Props = {
  actionType: "create" | "edit" | "view" | "clone";
  selectedData: WorkflowListData[];
  onDone(): void;
  idFromRowOption: React.ReactText | null;
  submitButtonText?: string;
  hideActionButton?: boolean;
  autoMenuActionsCode?: CrudOptions;
  clickedIdIfMultiIds?: string | number;
};

const fetchConfig = (
  selectedRows: WorkflowListData[],
  idFromRowOption?: React.ReactText | null,
  clickedIdIfMultiIds?: number | string
) => {
  const id = selectedRows.find(
    (r) =>
      String(r[idColumn]) === String(clickedIdIfMultiIds) ||
      Number(r[idColumn]) === Number(clickedIdIfMultiIds)
  )?.[idColumn];

  return {
    params: {
      Id:
        idFromRowOption ??
        (clickedIdIfMultiIds ? (id ?? selectedRows[0]?.[idColumn]) : selectedRows[0]?.[idColumn]),
    },
  };
};

export const workflowRightsRenderer = (): FieldRenderers<WorkflowFields> => ({
  boolean: booleanV2Renderer(false),
  singleselect: singleMultiSelectFieldRenderer("singleselect"),
  multiselect: singleMultiSelectFieldRenderer("multiselect"),
  file: fileUploadFieldRenderer(false, {
    imageURL: ["image/jpg", "image/png"],
  }),

  singleselectradio: singleMultiSelectFieldRenderer("singleselectradio"),
  singleselectsearchlistwithlinks: searchListFieldRenderer("singleSelectWithLinks", {
    searchEndpoint: endpointConfig,
    linkTextConfig: linkConfig,
    onLinkClick: onLinkClick,
    actionLinkConfig: actionLinkTextConfig,
    handleActionLinkClicked: onActionLinkClick,
  }),
  multiselectsearchlistwithlinks: searchListFieldRenderer("multiselectWithLinks", {
    searchEndpoint: endpointConfig,
    linkTextConfig: linkConfig,
    onLinkClick: onLinkClick,
    actionLinkConfig: actionLinkTextConfig,
    handleActionLinkClicked: onActionLinkClick,
  }),
  datetime: dateTimeV2Renderer(false),
  stringreadonly: stringReadOnlyEditRenderer(null, null, false),
  integerreadonly: stringReadOnlyEditRenderer(null, null, true),
  string: stringV2Renderer(false, null, null),
});

export const workflowReadOnlyRightsRenderer = (): FieldRenderers<WorkflowFields> => ({
  boolean: booleanV2Renderer(true),
  multiselect: singleMultiSelectReadOnlyFieldRenderer("multiselect"),
  file: fileUploadFieldRenderer(true),
  datetime: dateTimeV2Renderer(true),
  singleselectsearchlistwithlinks: searchListReadOnlyFieldRenderer("singleselect"),
  custom: workflowBuilderFieldRenderer(true),
  string: stringV2Renderer(true, null, null),
});

function WorkflowConfiguration(props: Props): JSX.Element {
  const {
    actionType,
    selectedData,
    idFromRowOption,
    submitButtonText,
    hideActionButton = false,
    autoMenuActionsCode,
    clickedIdIfMultiIds,
  } = props;

  let initialDataUrl =
    actionType === "edit"
      ? endpoints.workflowConfiguration.edit
      : actionType === "create"
        ? endpoints.workflowConfiguration.create
        : actionType === "view"
          ? endpoints.workflowConfiguration.view
          : actionType === "clone"
            ? endpoints.workflowConfiguration.clone
            : "";

  return (
    <Entity
      initialDataUrl={initialDataUrl}
      readOnly={actionType === "view"}
      submitUrl={initialDataUrl}
      onDone={props.onDone}
      selectedData={selectedData}
      idFromRowOption={idFromRowOption}
      formType={"json"}
      readOnlyFields={
        actionType === "create" || actionType === "edit"
          ? [
              {
                fieldName: "Id",
                readOnlyRenderer: "integerreadonly",
              },
            ]
          : undefined
      }
      fieldRenderers={workflowRightsRenderer}
      validationSchema={workflowValidationSchema}
      readOnlyFieldRenderers={workflowReadOnlyRightsRenderer}
      nullStringValues={[]}
      fetchConfig={actionType === "create" ? undefined : fetchConfig}
      submitButtonText={submitButtonText}
      hideActionButton={hideActionButton}
      hiddenFields={[]}
      autoMenuActionsCode={
        actionType.toLowerCase() === autoMenuActionsCode?.toLowerCase()
          ? autoMenuActionsCode
          : undefined
      }
      clickedIdIfMultiIds={clickedIdIfMultiIds}
    />
  );
}

export default WorkflowConfiguration;
